import React from 'react';
import DOMPurify from 'dompurify';

interface ArticleDisplayProps {
    data: any;
    activeTab: 'article' | 'historique' | 'references';
}

export const ArticleDisplay: React.FC<ArticleDisplayProps> = ({ data, activeTab }) => {
    const { article, code, hierarchie, modifications, references } = data;

    const formatDate = (dateString: any) => {
        if (!dateString || dateString === null || dateString === undefined) return '—';
        
        try {
            const dateStr = String(dateString);
            
            // Si c'est un timestamp
            if (/^\d+$/.test(dateStr)) {
                const timestamp = parseInt(dateStr);
                // Vérifier que le timestamp est valide
                if (isNaN(timestamp) || timestamp < 0) return '—';
                
                const date = new Date(timestamp);
                // Vérifier que la date est valide
                if (isNaN(date.getTime())) return '—';
                
                const year = date.getFullYear();
                
                // Si la date est au-delà de 2500, afficher l'infini
                if (year > 2500) {
                    return '∞';
                }
                
                return new Intl.DateTimeFormat('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }).format(date);
            }
            
            // Si c'est une date ISO string
            if (dateStr.includes('T') || dateStr.includes('-')) {
                const date = new Date(dateStr);
                // Vérifier que la date est valide
                if (isNaN(date.getTime())) return '—';
                
                const year = date.getFullYear();
                
                if (year > 2500) {
                    return '∞';
                }
                
                return new Intl.DateTimeFormat('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }).format(date);
            }
            
            // Si c'est déjà une chaîne formatée, la retourner
            return dateStr;
        } catch (error) {
            console.warn('Erreur lors du formatage de la date:', dateString, error);
            return '—';
        }
    };

    // Fonction pour décoder les entités HTML
    const decodeHTML = (html: string) => {
        if (!html) return '';
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
    };

    const renderArticleTab = () => (
        <div className="space-y-3">
            {/* Code juridique - Simple titre */}
            {code && (
                <h3 className="font-platypi text-lg text-mcdm-third-500 dark:text-mcdm-primary-100 pb-2 border-b border-gray-200 dark:border-gray-600">
                    {code.nom}
                </h3>
            )}

            {/* Hiérarchie - Plus élégant */}
            {hierarchie?.cheminComplet && (
                <div className="text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex flex-wrap items-center gap-1">
                        {decodeHTML(hierarchie.cheminComplet).split('>').map((part, index, array) => (
                            <React.Fragment key={index}>
                                <span className={index === array.length - 1 ? 'font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100' : ''}>
                                    {part.trim()}
                                </span>
                                {index < array.length - 1 && (
                                    <span className="text-gray-400">›</span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            {/* Article principal */}
            <div className="bg-white dark:bg-mcdm-third-300 rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h4 className="font-platypi text-base text-mcdm-third-500 dark:text-mcdm-primary-100">
                            Article {article.titre}
                        </h4>
                        {article.version && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                v{article.version}
                            </span>
                        )}
                    </div>
                    {article.etat && (
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                            article.etat === 'VIGUEUR' 
                                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                                : 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                            {article.etat.toLowerCase()}
                        </span>
                    )}
                </div>

                {/* Nota */}
                {article.notaHtml && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-3 mb-4">
                        <div 
                            className="text-sm text-amber-900 dark:text-amber-200"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.notaHtml) }}
                        />
                    </div>
                )}

                {/* Texte de l'article */}
                <div className="mb-4">
                    {article.texteHtml ? (
                        <div
                            className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.texteHtml) }}
                        />
                    ) : (
                        <div className="text-sm leading-relaxed whitespace-pre-wrap">
                            {article.texte}
                        </div>
                    )}
                </div>

                {/* Période de validité */}
                <div className="flex items-center gap-4 pt-3 border-t border-gray-100 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                        <span>Depuis</span>
                        <span className="font-medium">{formatDate(article.dateDebut)}</span>
                    </div>
                    {article.dateFin && (
                        <>
                            <span>→</span>
                            <div className="flex items-center gap-1">
                                <span>Jusqu'au</span>
                                <span className="font-medium">{formatDate(article.dateFin)}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    const renderHistoriqueTab = () => (
        <div className="space-y-3">
            {modifications?.lienModifications && modifications.lienModifications.length > 0 && (
                <div>
                    <h4 className="font-platypi text-base text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                        Modifications
                    </h4>
                    <div className="space-y-2">
                        {modifications.lienModifications.map((mod: any, index: number) => (
                            <div key={index} className="bg-white dark:bg-mcdm-third-300 p-3 rounded-lg">
                                <div className="text-sm font-medium mb-1">{mod.textTitle || '—'}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {mod.linkType || '—'} • {formatDate(mod.datePubliTexte)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {modifications?.articleVersions && modifications.articleVersions.length > 0 && (
                <div>
                    <h4 className="font-platypi text-base text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                        Historique des versions
                    </h4>
                    <div className="space-y-2">
                        {modifications.articleVersions.map((version: any, index: number) => (
                            <div key={index} className="bg-white dark:bg-mcdm-third-300 p-3 rounded-lg flex justify-between items-center">
                                <span className="text-sm font-medium">v{version.version || '—'}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDate(version.dateDebut)} → {formatDate(version.dateFin)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderReferencesTab = () => (
        <div className="space-y-3">
            {references?.lienCitations && references.lienCitations.length > 0 && (
                <div>
                    <h4 className="font-platypi text-base text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                        Cité par
                    </h4>
                    <div className="space-y-2">
                        {references.lienCitations.map((cite: any, index: number) => (
                            <div key={index} className="bg-white dark:bg-mcdm-third-300 p-3 rounded-lg">
                                <div className="text-sm mb-1">{cite.textTitle || '—'}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Article {cite.articleNum || '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {references?.lienConcordes && references.lienConcordes.length > 0 && (
                <div>
                    <h4 className="font-platypi text-base text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                        Concordances
                    </h4>
                    <div className="space-y-2">
                        {references.lienConcordes.map((concorde: any, index: number) => (
                            <div key={index} className="bg-white dark:bg-mcdm-third-300 p-3 rounded-lg">
                                <div className="text-sm">{concorde.textTitle || '—'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="py-2">
            {activeTab === 'article' && renderArticleTab()}
            {activeTab === 'historique' && renderHistoriqueTab()}
            {activeTab === 'references' && renderReferencesTab()}
        </div>
    );
};

export default ArticleDisplay;